/**
 * Content-free interaction telemetry. The product needs to know whether a
 * card move felt immediate; it never needs to store a player's cards, room or
 * opponents to answer that question. A host analytics adapter can subscribe to
 * the browser event later, while the default implementation remains local.
 */
export type InteractionName =
  | 'draw'
  | 'discard'
  | 'declare'
  | 'drop'
  | 'show'
  | 'arrange'
  | 'reconnect-catchup';

export interface InteractionSample {
  name: InteractionName;
  outcome: 'confirmed' | 'rejected' | 'abandoned';
  durationMs: number;
}

const active = new Map<string, { name: InteractionName; startedAt: number }>();
const samples: InteractionSample[] = [];

export const interactionMetrics = {
  begin(id: string, name: InteractionName): void {
    active.set(id, { name, startedAt: performance.now() });
  },

  settle(id: string, outcome: InteractionSample['outcome']): void {
    const current = active.get(id);
    if (!current) return;
    active.delete(id);
    const sample: InteractionSample = {
      name: current.name,
      outcome,
      durationMs: Math.round(performance.now() - current.startedAt),
    };
    samples.push(sample);
    if (samples.length > 120) samples.splice(0, samples.length - 120);
    window.dispatchEvent(new CustomEvent<InteractionSample>('rummy:interaction', { detail: sample }));
  },

  /** Useful for a local debug panel or an explicitly connected analytics sink. */
  snapshot(): readonly InteractionSample[] {
    return samples;
  },
};
